import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Divider, Form, Input, Select, Switch, message } from "antd";
import { BiSolidPlusSquare } from "react-icons/bi";
import { MdOutlineDeleteForever } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";
import { getAllLocations } from "../../../network/location";
import {
  addLoyaltyProgram,
  updateLoyaltyProgram,
} from "../../../network/settings";
import queryClient from "../../../services/react-query/queryClient";
import { queryKeys } from "../../../services/react-query/queryKeys";

type Props = { close: any; dataProgram: any };

const ModalLoyaltyProgram = ({ close, dataProgram }: Props) => {
  const [form] = Form.useForm();
  const { data, isLoading } = useQuery([queryKeys.listLocations], () =>
    getAllLocations()
  );

  const { mutate: addProgram, isLoading: isAdding } = useMutation(
    addLoyaltyProgram,
    {
      onSuccess: (res: any) => {
        if (res?.code === "ERR_BAD_REQUEST") {
          message.error(res?.response?.data?.message);
        } else {
          console.log(res, "res");
          message.success("loyalty added successfully");
          queryClient.invalidateQueries(["loyalty"]);
          close();
        }
      },
    }
  );
  const { mutate: updatePayment, isLoading: isUpdateing } = useMutation(
    updateLoyaltyProgram,
    {
      onSuccess: (res: any) => {
        if (res?.code === "ERR_BAD_REQUEST") {
          message.error(res?.response?.data?.message);
        } else {
          console.log(res, "res");
          message.success("loyalty updated successfully");
          queryClient.invalidateQueries(["loyalty"]);
          close();
        }
      },
    }
  );
  const options: any = data?.data?.map((item: any, i: any) => ({
    label: item?.name,
    value: item?.id,
  }));
  const onFinish = (values: any) => {
    console.log("Form submit values:", values); // Debug log
    if (values?.ranges?.length > 1 && values?.ranges?.length < 4) {
      values?.ranges.pop();
    }
    dataProgram
      ? updatePayment({ id: dataProgram?.id, ...values })
      : addProgram(values);
    console.log(values);
  };
  const handleAdd = (add: any) => {
    // Retrieve current form values
    form
      .validateFields()
      .then((values) => {
        // Check if any of the existing fields are empty
        const allFilled = values.ranges.every(
          (name: any) =>
            name.from_amount && name.to_amount && name.discount_percentage
        );
        console.log(allFilled);
        console.log(values.ranges);

        if (allFilled) add();
        else message.error("must add values");
        return;
      })
      .catch((err) => {
        console.log(err);
      });
  };
  const intl = useIntl();
  const validity_period_options = [
    { label: intl.formatMessage({ id: "oneYear" }), value: "one_year" },
    { label: intl.formatMessage({ id: "six_months" }), value: "six_months" },
    {
      label: intl.formatMessage({ id: "three_months" }),
      value: "three_months",
    },
    { label: intl.formatMessage({ id: "one_month" }), value: "one_month" },
  ];
  const locationIds = dataProgram?.locations?.map(
    (location: any) => location?.id
  );

  return (
    <div>
      <div className="mb-6 text-center">
        <h4 className="text-2xl font-semibold text-color-primary">
          <FormattedMessage
            id={dataProgram ? "loyaltyProgram.update" : "loyaltyProgram.add"}
          />{" "}
          {dataProgram && " - " + dataProgram?.name}
        </h4>
        <p className="text-base text-[#75757E] mt-5">
          {!!dataProgram || <FormattedMessage id={"loyaltyProgram.add.info"} />}
        </p>
      </div>
      <Form
        name="step1"
        form={form}
        onFinish={onFinish}
        layout={"vertical"}
        initialValues={{
          is_active: dataProgram?.is_active || false,
          name: dataProgram?.name || "",
          validity_period: dataProgram?.validity_period || "",
          location_ids: locationIds || [],
          ranges: dataProgram?.ranges || [{ from_amount: "", to_amount: "" }],
        }}
      >
        <div className="flex items-center gap-4">
          <Form.Item
            label={<FormattedMessage id="program.nameLabel" />}
            name="name"
            required={false}
            className="w-full max-md:w-full max-xl:w-[25rem]"
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: "program.nameRequired" }),
              },
            ]}
            hasFeedback
          >
            <Input
              placeholder={intl.formatMessage({
                id: "program.namePlaceholder",
              })}
            />
          </Form.Item>
          <Form.Item
            label={
              <div className="text-sm font-medium">
                <FormattedMessage id="program.locationsLabel" />
              </div>
            }
            required={false}
            name="location_ids"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "program.locationsRequired",
                }),
              },
            ]}
            className="w-full max-md:w-full max-xl:w-[25rem]"
          >
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder={intl.formatMessage({
                id: "program.locationsPlaceholder",
              })}
              options={options}
            />
          </Form.Item>
          <Form.Item
            label={
              <div className="text-sm font-medium">
                <FormattedMessage id="ProgramValidityLabel" />
              </div>
            }
            required={false}
            name="validity_period"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "SelectApplicationValidity",
                }),
              },
            ]}
            className="w-full max-md:w-full max-xl:w-[25rem]"
          >
            <Select
              style={{ width: "100%" }}
              placeholder={intl.formatMessage({
                id: "SelectApplicationValidity",
              })}
              options={validity_period_options}
            />
          </Form.Item>
        </div>
        <Divider className="bg-[#EEF0FF] mt-0 !mb-4" />
        <div className="mb-4 text-base font-medium text-color-primary">
          <FormattedMessage id="discount.defineRangeAndValue" />
        </div>
        <Form.List
          name="ranges"
          rules={[
            {
              validator: async (_, names) => {
                if (!names || names.length < 1) {
                  return Promise.reject(
                    new Error("At least one set of values is required.")
                  );
                }
              },
            },
          ]}
        >
          {(fields, { add, remove }, { errors }) => {
            const updateDiscounts = () => {
              const currentRanges = form.getFieldValue("ranges");
              const updatedRanges = currentRanges.map(
                (range: any, index: number) => ({
                  ...range,
                  discount_percentage: 5 + 5 * index,
                })
              );
              form.setFieldsValue({ ranges: updatedRanges });
            };
            const handleRemove = (index: number) => {
              remove(index);
              setTimeout(() => {
                updateDiscounts();
              }, 0);
            };
            return (
              <>
                <div className="flex items-end gap-2">
                  <div className="flex flex-col gap-2">
                    {fields.map((field, index) => {
                      return (
                        <div
                          key={field?.key}
                          className="flex items-center justify-between gap-4"
                        >
                          <div className="flex items-center gap-4">
                            <Form.Item
                              {...field}
                              label={
                                <div
                                  className={
                                    fields.length - 1 === index
                                      ? "text-[#4F5062]"
                                      : "text-[#75757E]"
                                  }
                                >
                                  {intl.formatMessage({
                                    id: "field.fromValue",
                                  })}
                                </div>
                              }
                              rules={[
                                {
                                  validator: (rule, value) => {
                                    const ranges_value =
                                      form.getFieldValue("ranges");

                                    if (index !== 0 && value) {
                                      const program_value_to =
                                        ranges_value[index - 1]?.to_amount;
                                      console.log(value, program_value_to);
                                      if (+value > +program_value_to) {
                                        return Promise.resolve();
                                      }
                                      return Promise.reject(
                                        new Error(
                                          `${intl.formatMessage({
                                            id: "error.valueMustBeGreaterThan",
                                          })} ${program_value_to} ${intl.formatMessage(
                                            { id: "error.toFormCorrectRange" }
                                          )}`
                                        )
                                      );
                                    } else {
                                      return Promise.resolve();
                                    }
                                  },
                                },
                              ]}
                              name={[field.name, "from_amount"]}
                              required={false}
                              className="w-[14rem]"
                            >
                              <Input
                                suffix={
                                  <div className="!text-xs text-color-primary">
                                    {intl.formatMessage({
                                      id: "SAR",
                                    })}{" "}
                                  </div>
                                }
                                type="number"
                                className={
                                  fields.length > 1 &&
                                  index !== fields.length - 1
                                    ? "bg-[#EEF0FF] border-none focus:border-none"
                                    : ""
                                }
                              />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              required={false}
                              className="w-[14rem]"
                              rules={[
                                {
                                  validator: (rule, value) => {
                                    const program_value_to =
                                      form.getFieldValue("ranges");
                                    const fromAmount =
                                      program_value_to[index]?.from_amount;

                                    if (
                                      +value <
                                        program_value_to[index]?.from_amount &&
                                      +value
                                    ) {
                                      return Promise.reject(
                                        new Error(
                                          `${intl.formatMessage({
                                            id: "error.valueMustBeGreaterOrEqual",
                                          })} ${fromAmount} ${intl.formatMessage(
                                            {
                                              id: "error.valueMustBeGreaterOrEqual2",
                                            }
                                          )}`
                                        )
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                              label={
                                <div
                                  className={
                                    fields.length - 1 === index
                                      ? "text-[#4F5062]"
                                      : "text-[#75757E]"
                                  }
                                >
                                  {intl.formatMessage({
                                    id: "field.toValue",
                                  })}{" "}
                                </div>
                              }
                              name={[field.name, "to_amount"]}
                            >
                              <Input
                                suffix={
                                  <div className="!text-xs text-color-primary">
                                    {intl.formatMessage({
                                      id: "SAR",
                                    })}
                                  </div>
                                }
                                type="number"
                                className={
                                  fields.length > 1 &&
                                  index !== fields.length - 1
                                    ? "bg-[#EEF0FF] border-none focus:border-none"
                                    : ""
                                }
                              />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              className="w-[14rem]"
                              required={false}
                              label={
                                <div
                                  className={
                                    fields.length - 1 === index
                                      ? "text-[#4F5062]"
                                      : "text-[#75757E]"
                                  }
                                >
                                  {intl.formatMessage({
                                    id: "field.discountPercentage",
                                  })}
                                </div>
                              }
                              initialValue={index === 3 ? 25 : 5 * (index + 1)}
                              name={[field.name, "discount_percentage"]}
                            >
                              <Input
                                disabled={true}
                                style={{
                                  color: "text-[#4F5062]",
                                  border: "0px solid none",
                                  background: "#B0B0C04D",
                                }}
                                prefix={
                                  <div className="text-sm text-[#4F5062]">
                                    %
                                  </div>
                                }
                                className={
                                  "!border-none !text-[#4F5062] cursor-default"
                                }
                              />
                            </Form.Item>
                          </div>
                          {fields.length > 1 && index !== fields.length - 1 && (
                            <MdOutlineDeleteForever
                              className="text-[#C43535] cursor-pointer"
                              onClick={() => handleRemove(index)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {fields.length < 4 && (
                    <BiSolidPlusSquare
                      className="mb-8 text-3xl rounded-lg cursor-pointer text-color-primary"
                      onClick={() => handleAdd(add)}
                    />
                  )}
                </div>
                <Form.ErrorList errors={errors} />
              </>
            );
          }}
        </Form.List>
        <div className="flex items-center justify-between mb-12">
          <div className="flex flex-col gap-2">
            <span className="text-sm text-color-primary">
              {intl.formatMessage({
                id: "settings12setCurrentProgram",
              })}
            </span>
            <p className="text-[#75757E] text-xs">
              {intl.formatMessage({
                id: "settings12programInfo",
              })}
            </p>
          </div>
          <Form.Item
            className="flex items-center !m-0"
            valuePropName="checked"
            name="is_active"
          >
            <Switch className="flex items-center !m-0" />
          </Form.Item>
        </div>
        <div className="flex items-center gap-4">
          <Button
            type="primary"
            className="w-full"
            htmlType="submit"
            loading={isAdding}
          >
            <FormattedMessage
              id={dataProgram ? "button.updateProgram" : "button.addProgram"}
            />
          </Button>
          <Button
            type="primary"
            onClick={() => close()}
            className="w-full btn-primary-not"
            htmlType="button"
          >
            {intl.formatMessage({
              id: "cancel",
            })}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ModalLoyaltyProgram;
